import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Chip,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const DEBOUNCE_MS = 400;

interface Agravo {
  id: number;
  data: string;
  codigo_cid: string;
  nome_cid: string;
  grupo_codigo: string;
  grupo_nome: string;
  grupo_cor: string;
  faixa_cid: string;
  regiao_id: number;
  regiao_nome: string;
  codigo_unidade: number;
  nome_unidade: string;
  semana_epi: number;
  ano: number;
  total: number;
  total_registros: number;
}

interface Grupo {
  codigo_grupo: string;
  nome: string;
  faixa_cid: string;
  cor_hex: string;
}

interface Regiao {
  id: number;
  nome: string;
}

interface Filtros {
  grupo_cid: string;
  regiao_id: string;
  data_inicio: string;
  data_fim: string;
}

const toISO = (d: Date): string => d.toISOString().split('T')[0];

const MonitoramentoVigilanciaEpidemiologicaDetalhes: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const navigate = useNavigate();

  const hoje = new Date();
  const doisMeses = new Date();
  doisMeses.setMonth(doisMeses.getMonth() - 2);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filtros, setFiltros] = useState<Filtros>({
    grupo_cid: '',
    regiao_id: '',
    data_inicio: toISO(doisMeses),
    data_fim: toISO(hoje),
  });
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');

  const [agravos, setAgravos] = useState<Agravo[]>([]);
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [regioes, setRegioes] = useState<Regiao[]>([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const buscaTimer = useRef<any>(null);

  // Carrega catalogos uma vez
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    Promise.all([
      monitoramentoService.getVigilanciaGruposCid(municipioAtivo.id),
      monitoramentoService.getVigilanciaRegioes(municipioAtivo.id),
    ]).then(([g, r]) => {
      setGrupos(g);
      setRegioes(r);
    }).catch(() => {});
  }, [municipioAtivo?.id]);

  useEffect(() => {
    if (buscaTimer.current) clearTimeout(buscaTimer.current);
    buscaTimer.current = setTimeout(() => setBuscaDebounced(busca), DEBOUNCE_MS);
    return () => { if (buscaTimer.current) clearTimeout(buscaTimer.current); };
  }, [busca]);

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const params: any = {
          ...(filtros.grupo_cid ? { grupo_cid: filtros.grupo_cid } : {}),
          ...(filtros.regiao_id ? { regiao_id: filtros.regiao_id } : {}),
          ...(filtros.data_inicio ? { data_inicio: filtros.data_inicio } : {}),
          ...(filtros.data_fim ? { data_fim: filtros.data_fim } : {}),
          ...(buscaDebounced ? { busca: buscaDebounced } : {}),
          page: page + 1,
          limit: rowsPerPage,
        };
        const res = await monitoramentoService.getVigilanciaAgravos(municipioAtivo.id, params);
        if (!cancelled) {
          setAgravos(res);
          setTotalRegistros(res?.[0]?.total_registros || 0);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || 'Erro ao buscar agravos.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [municipioAtivo?.id, filtros, buscaDebounced, page, rowsPerPage]);

  const handleFiltro = (k: keyof Filtros, v: string) => {
    setFiltros(f => ({ ...f, [k]: v }));
    setPage(0);
  };

  const exportParams = {
    ...(filtros.grupo_cid ? { grupo_cid: filtros.grupo_cid } : {}),
    ...(filtros.regiao_id ? { regiao_id: filtros.regiao_id } : {}),
    ...(filtros.data_inicio ? { data_inicio: filtros.data_inicio } : {}),
    ...(filtros.data_fim ? { data_fim: filtros.data_fim } : {}),
    ...(buscaDebounced ? { busca: buscaDebounced } : {}),
  } as Record<string, string>;

  if (!municipioAtivo) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">Selecione um municipio para visualizar agravos.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700} display="flex" alignItems="center" gap={1}>
            <CoronavirusIcon color="error" /> Agravos / Registros
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Listagem detalhada de agravos epidemiologicos - {municipioAtivo.nome}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/monitoramento/vigilancia-epidemiologica')}
            size="small"
          >
            Voltar
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            href={`/api${monitoramentoService.getVigilanciaCsvUrl(municipioAtivo.id, exportParams)}`}
            target="_blank"
            rel="noopener"
            size="small"
          >
            CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            href={`/api${monitoramentoService.getVigilanciaPdfUrl(municipioAtivo.id, municipioAtivo.nome, exportParams)}`}
            target="_blank"
            rel="noopener"
            size="small"
          >
            PDF
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Grupo CID</InputLabel>
              <Select
                value={filtros.grupo_cid}
                label="Grupo CID"
                onChange={(e) => handleFiltro('grupo_cid', e.target.value)}
              >
                <MenuItem value=""><em>Todos</em></MenuItem>
                {grupos.map(g => (
                  <MenuItem key={g.codigo_grupo} value={g.codigo_grupo}>
                    {g.nome} ({g.faixa_cid})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Regional</InputLabel>
              <Select
                value={filtros.regiao_id}
                label="Regional"
                onChange={(e) => handleFiltro('regiao_id', e.target.value)}
              >
                <MenuItem value=""><em>Todas</em></MenuItem>
                {regioes.map(r => (
                  <MenuItem key={r.id} value={String(r.id)}>{r.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth size="small"
              type="date"
              label="Data inicio"
              InputLabelProps={{ shrink: true }}
              value={filtros.data_inicio}
              onChange={(e) => handleFiltro('data_inicio', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth size="small"
              type="date"
              label="Data fim"
              InputLabelProps={{ shrink: true }}
              value={filtros.data_fim}
              onChange={(e) => handleFiltro('data_fim', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth size="small"
              label="Busca (CID ou nome)"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Ex: Dengue, A90..."
            />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        {loading ? <Skeleton variant="rounded" height={400} /> : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>CID</TableCell>
                  <TableCell>Nome do agravo</TableCell>
                  <TableCell>Grupo</TableCell>
                  <TableCell>Regional</TableCell>
                  <TableCell>Unidade</TableCell>
                  <TableCell align="center">Semana Epi</TableCell>
                  <TableCell align="right">Casos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agravos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        Nenhum agravo encontrado para os filtros selecionados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {agravos.map(a => (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.data}</TableCell>
                    <TableCell><strong>{a.codigo_cid}</strong></TableCell>
                    <TableCell>{a.nome_cid}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={a.grupo_nome || '-'}
                        sx={{ bgcolor: a.grupo_cor || '#9e9e9e', color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>{a.regiao_nome || '-'}</TableCell>
                    <TableCell>{a.nome_unidade || '-'}</TableCell>
                    <TableCell align="center">{a.semana_epi}/{a.ano}</TableCell>
                    <TableCell align="right"><strong>{Number(a.total).toLocaleString('pt-BR')}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalRegistros}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Linhas por pagina"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MonitoramentoVigilanciaEpidemiologicaDetalhes;
